import { startTransition, useOptimistic } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { fbt } from 'fbtee'
import { getNavTabs } from '@/lib/navTabs'
import { scrollToTop } from '@/hooks/useRouteScrollRestore'
import { emitTabReselect } from '@/hooks/useTabReselect'
import { useAdminPendingCount } from '../hooks/useAdminPendingCount'
import { useRequestsUnread } from '../hooks/useRequestsUnread'
import { useCrewNavBadgeCount } from '../hooks/useCrewNavBadgeCount'
import { useSelfProfile } from '../hooks/useSelfProfile'
import { NavBar, NavItem, NavProfileAvatar } from './NavBar'

export function BottomNav() {
  const location = useLocation()
  const navigate = useNavigate()
  // The tapped tab lights up (and the indicator starts sliding) before the next
  // page renders: the route change runs as a transition, so mounting a heavy tab
  // (Discover, Home) no longer holds the bar's first frame hostage.
  const [activePath, setActivePath] = useOptimistic(location.pathname)
  const switchTab = (to: string) => startTransition(async () => {
    setActivePath(to)
    await navigate(to, { replace: true })
  })
  const { isUser, profile, profileLoading } = useSelfProfile()
  const crewCount = useCrewNavBadgeCount()
  const adminPendingCount = useAdminPendingCount()
  const requestsUnread = useRequestsUnread(false)

  const profileActive = activePath === '/profile'

  return (
    <NavBar label={fbt('Primary', 'Accessible label for the primary bottom navigation')} className="md:hidden" fixed={false}>
      {getNavTabs().map(tab => {
        const active = activePath === tab.to
        return (
          <li key={tab.to} className="flex-1">
            <NavItem
              icon={tab.icon}
              label={tab.label}
              active={active}
              badge={tab.to === '/social' ? crewCount : 0}
              badgeTestId={tab.to === '/social' ? 'nav-crew-badge' : undefined}
              onClick={() => {
                if (active) {
                  scrollToTop(location.pathname)
                  emitTabReselect(tab.to)
                  return
                }
                switchTab(tab.to)
              }}
            />
          </li>
        )
      })}
      <li className="flex-1">
        <NavItem
          label={fbt('Profile', 'Bottom nav label for the profile tab')}
          active={profileActive}
          badge={adminPendingCount + requestsUnread}
          badgeTestId="nav-profile-badge"
          visual={<NavProfileAvatar isUser={isUser} profile={profile} profileLoading={profileLoading} active={profileActive} />}
          onClick={() => {
            if (profileActive) {
              scrollToTop(location.pathname)
              return
            }
            switchTab('/profile')
          }}
        />
      </li>
    </NavBar>
  )
}
